import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';

const customStyles = `
.rich-text-editor {
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

/* Toolbar styling */
.toolbar {
  display: flex;
  gap: 5px;
  padding: 10px;
  background-color: #f3f4f6;
  border-bottom: 1px solid #ddd;
}

.toolbar button {
  padding: 6px 12px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s;
}

.toolbar button:hover {
  background-color: #e5e7eb;
}

/* The 'is-active' class makes the button look pressed/black */
.toolbar button.is-active {
  background-color: #000;
  color: #fff;
}

/* Input area styling */
.ProseMirror {
  padding: 15px;
  min-height: 150px; /* Adjust height as needed */
  outline: none;     /* Removes the default browser blue outline */
}

.ProseMirror ul {
  padding-left: 20px;
  list-style-type: disc;
}
  .ProseMirror em, 
.ProseMirror i {
  font-style: italic !important;
}

/* Force Underline to have a line */
.ProseMirror u {
  text-decoration: underline !important;
}

/* Force Bold to look bold (just in case) */
.ProseMirror strong, 
.ProseMirror b {
  font-weight: bold !important;
}

/* Force Lists to show bullets */
.ProseMirror ul {
  list-style-type: disc !important;
  padding-left: 1.5rem !important;
  margin: 1rem 0;
}

.ProseMirror ol {
  list-style-type: decimal !important;
  padding-left: 1.5rem !important;
  margin: 1rem 0;
}
  .font-size-select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  outline: none;
}

.font-size-select:hover {
  border-color: #bbb;
}
`
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize.replace('px', ''),
        renderHTML: attributes => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}px` };
        },
      },
    };
  },
});
export const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editor = useEditor({
    
    extensions: [
      StarterKit,
      Underline, // Added specifically for your request
      TextStyle,
      FontSize
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'editor-input', // Class for the actual input area
      },
    },
    onUpdate: ({ editor }) => {
      // Send HTML back to parent
      onChange(editor.getHTML());
    },
  });

  // This useEffect ensures that if you load data from an API (like topic contents),
  // the editor updates to show it.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Only update if the content is actually different to prevent cursor jumping
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
    <style>{customStyles}</style>
    <div className="rich-text-editor">
      {/* Toolbar */}
      <div className="toolbar">
        <select
    onChange={(e) => editor.chain().focus().setMark('textStyle', { fontSize: e.target.value }).run()}
    className="font-size-select"
    defaultValue="16"
  >
    <option value="12">12px</option>
    <option value="14">14px</option>
    <option value="16">16px</option>
    <option value="18">18px</option>
    <option value="20">20px</option>
    <option value="24">24px</option>
    <option value="30">30px</option>
  </select>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic"
        >
          <em>I</em>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title="Underline"
        >
          <u>U</u>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
        >
          • List
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
    </>
  );
};